/* assets/col-grid-pagination.js
   Shared by the collection grid (sections/collection-product-grid.liquid) and
   the search results grid (sections/search.liquid). It lives in an asset, not
   in either section's {% javascript %} block, because that bundle is gated per
   section - `if (!__sections__["collection-product-grid"]) return;` - so the
   collection's copy would never run on /search. Both sections load this file
   themselves; the guard below keeps a double include harmless.

   Owns three things for any .col-grid on the page:
     1. deferring the ragged part-row while more pages exist,
     2. Load more (an <a> to the next paginated URL, appended in place; the
        next page is requested as the grid's section only, and prefetched
        while the customer scrolls towards the button),
     3. rebuilding those pages, and the scroll position, on a Back navigation.
*/
(function () {
  if (window.__colGridInit) return;
  window.__colGridInit = true;

  // ── Defer the trailing part-row ──────────────────────────────────────────
  // Pagination counts products; the grid lays out cards, and one product can
  // render several size cards - so a page never lands on a whole number of
  // rows. Hide the leftover part-row while more pages exist; it reappears as
  // soon as Load more brings enough cards to complete it, and the final page
  // always shows everything (nothing is ever permanently hidden).
  //
  // Measures real grid cells rather than child count: a wide lifestyle tile
  // spans 2 columns, so nth-child maths would be wrong.
  function cellsOf(el) {
    return el.classList.contains('col-grid__life--wide') ? 2 : 1;
  }

  function trimSection(section) {
    var grid = section.querySelector('.col-grid__items');
    if (!grid) return;

    var items = grid.children;
    var i;
    for (i = 0; i < items.length; i++) items[i].classList.remove('is-row-stub');

    // Last page in: show the remainder, ragged or not.
    if (!section.querySelector('.col-grid__load-more')) return;

    var cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
    if (!cols) return;

    var cells = 0;
    for (i = 0; i < items.length; i++) cells += cellsOf(items[i]);

    // Hide from the end until what remains is a whole number of rows. Looping
    // on the total (rather than subtracting a remainder) keeps it correct when
    // a 2-cell wide tile straddles the boundary.
    for (i = items.length - 1; i >= 0 && cells % cols !== 0; i--) {
      items[i].classList.add('is-row-stub');
      cells -= cellsOf(items[i]);
    }
  }

  // Coalesce with a timeout rather than rAF: rAF is skipped entirely while the
  // tab isn't painting, which would leave the part-row showing. getComputedStyle
  // forces the layout this needs regardless.
  var queued = false;
  function trimAll() {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      var sections = document.querySelectorAll('.col-grid');
      for (var i = 0; i < sections.length; i++) trimSection(sections[i]);
      // Same trigger, same reason: a filter swap brings a new Load more link.
      observeLoadMore();
    }, 0);
  }

  // Re-run whenever cards change (Load more appends; the filter bar replaces
  // the whole section's innerHTML) and whenever the column count changes.
  // Watches the persistent shopify-section wrapper, which survives that swap.
  function watch() {
    var grids = document.querySelectorAll('.col-grid');
    for (var i = 0; i < grids.length; i++) {
      var wrap = grids[i].closest('[id^="shopify-section-"]') || grids[i].parentNode;
      if (!wrap || wrap.__colGridWatched) continue;
      wrap.__colGridWatched = true;
      // childList only - trim toggles classes, so it can't retrigger this.
      new MutationObserver(trimAll).observe(wrap, { childList: true, subtree: true });
    }
    trimAll();
  }

  // ── Load more, and rebuilding it on Back ─────────────────────────────────
  // The trigger is an <a href> to the next paginated URL, so every page is
  // crawlable and the control degrades to a plain page load. Here we intercept
  // the click and append instead - and record how many pages are open, so a
  // customer who opens a product from deep in the grid and hits Back lands
  // back on the same products at the same scroll position.

  var STATE_PREFIX = 'colgrid:';
  var STATE_TTL = 30 * 60 * 1000; // stale state is worse than none
  var RESTORE_CAP = 10; // ceiling on the request burst a rebuild fires

  // One entry per filter/sort combination: the same collection under a
  // different filter is a different run of pages. `page` is dropped so page 1
  // and page 3 of the same list share the entry.
  function stateKey() {
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    return STATE_PREFIX + url.pathname + url.search;
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(stateKey());
      if (!raw) return null;
      var state = JSON.parse(raw);
      if (!state || !state.t || Date.now() - state.t > STATE_TTL) return null;
      return state;
    } catch (e) {
      return null;
    }
  }

  function writeState(pages, scroll) {
    try {
      sessionStorage.setItem(
        stateKey(),
        JSON.stringify({ pages: pages, scroll: scroll, t: Date.now() })
      );
    } catch (e) {
      /* private mode / quota - the grid just won't rebuild */
    }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(stateKey());
    } catch (e) {}
  }

  function pageOf(url) {
    var n = parseInt(new URL(url, window.location.href).searchParams.get('page'), 10);
    return n > 1 ? n : 1;
  }

  function pageUrl(n) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', n);
    return url.toString();
  }

  function setBusy(link, busy) {
    if (!link) return;
    var label = link.querySelector('[data-load-more-label]');
    if (busy) {
      if (label) {
        link.setAttribute('data-idle-label', label.textContent);
        var loading = (link.getAttribute('data-loading-label') || '').trim();
        label.textContent = loading || label.textContent;
      }
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('aria-busy', 'true');
    } else {
      if (label && link.hasAttribute('data-idle-label')) {
        label.textContent = link.getAttribute('data-idle-label');
      }
      link.removeAttribute('aria-disabled');
      link.removeAttribute('aria-busy');
    }
  }

  // ── Fetching a page: the grid's section only, memoised ───────────────────
  // Only .col-grid__items and the next Load more link are used from a fetched
  // page, so ask the Section Rendering API for the grid's own section
  // (?section_id=) instead of the whole document - header mega menu, filter
  // bar and footer included, that is ~8x the bytes and main-thread parse.
  // The section id comes off the grid (collection) or the section around it
  // (search), with the Shopify wrapper id as the last resort. With none at all
  // the whole page is fetched as before; both carry the same grid markup.
  function sectionIdOf(grid) {
    if (!grid) return '';
    var id = grid.getAttribute('data-section-id');
    if (id) return id;
    var host = grid.closest('[data-section-id]');
    if (host) return host.getAttribute('data-section-id');
    var wrap = grid.closest('[id^="shopify-section-"]');
    return wrap ? wrap.id.replace(/^shopify-section-/, '') : '';
  }

  // The rendering parameters only ever belong on the request. Anything written
  // back to the page - the Load more href, which is also the no-JS link and
  // what a modified click opens - stays a real, shareable page URL.
  function withoutSectionParams(href) {
    if (!href || !/[?&]sections?(_id)?=/.test(href)) return href;
    var url = new URL(href, window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    return url.pathname + url.search + url.hash;
  }

  function requestUrl(href, sectionId) {
    var url = new URL(withoutSectionParams(href), window.location.href);
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  // One request per URL, shared by the prefetch and the tap that follows it.
  // Entries expire so a page restored from the back/forward cache much later
  // doesn't append stale prices; a failed request is dropped so a retry refetches.
  var CACHE_TTL = 5 * 60 * 1000;
  var pageCache = new Map();

  // `background` marks a prefetch: fetched at low priority, behind the card
  // images the customer is looking at (browsers without the hint ignore it).
  function loadHtml(url, background) {
    var hit = pageCache.get(url);
    if (hit && Date.now() - hit.t < CACHE_TTL) return hit.html;
    var init = { headers: { 'X-Requested-With': 'XMLHttpRequest' } };
    if (background) init.priority = 'low';
    var html = fetch(url, init).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    });
    pageCache.set(url, { t: Date.now(), html: html });
    html.catch(function () {
      var entry = pageCache.get(url);
      if (entry && entry.html === html) pageCache.delete(url);
    });
    return html;
  }

  // Parsed per use, never cached: appendPage moves the nodes out of the doc.
  function fetchPage(href, section) {
    return loadHtml(requestUrl(href, sectionIdOf(section))).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  // ── Prefetching the next page ─────────────────────────────────────────────
  // Start the next page's request while the customer is still ~800px above
  // Load more, so the tap usually appends from memory. Skipped where the
  // customer asked to save data or the connection is 2G, and where there is
  // no IntersectionObserver - the tap then simply fetches, as it always did.
  var prefetchObserver = null;

  function prefetchAllowed() {
    if (!('IntersectionObserver' in window)) return false;
    var c = navigator.connection;
    return !(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
  }

  function prefetch(link) {
    if (link.getAttribute('aria-disabled') === 'true') return; // a fetch is already out
    var section = link.closest('.col-grid');
    var href = link.getAttribute('href');
    if (!section || !href) return;
    loadHtml(requestUrl(href, sectionIdOf(section)), true).catch(function () {
      /* the tap will retry, or fall back to the real page */
    });
  }

  // Idempotent; runs on init and after every grid mutation, so a link the
  // filter bar swapped in is picked up too. Held until the page has loaded:
  // on a wide screen the button can sit inside the band from the start, and
  // the next page must not compete with the first screen's images. Then held
  // again until the customer has scrolled (or the page opened scrolled): on a
  // 900px desktop the button starts inside the band, so without this every
  // view - a bounce, a bot - paid for a page-2 render it never looked at. A
  // tap before any scroll simply fetches, as it did before prefetching.
  var waitingForLoad = false;
  var waitingForScroll = false;
  var scrolled = false;
  function onFirstScroll() {
    scrolled = true;
    observeLoadMore();
  }
  function observeLoadMore() {
    if (!prefetchAllowed()) return;
    if (document.readyState !== 'complete') {
      if (!waitingForLoad) {
        waitingForLoad = true;
        window.addEventListener('load', observeLoadMore);
      }
      return;
    }
    if (!scrolled && window.scrollY > 0) scrolled = true;
    if (!scrolled) {
      if (!waitingForScroll) {
        waitingForScroll = true;
        window.addEventListener('scroll', onFirstScroll, { passive: true, once: true });
      }
      return;
    }
    if (!prefetchObserver) {
      prefetchObserver = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) prefetch(entries[i].target);
          }
        },
        { rootMargin: '800px 0px' }
      );
    }
    var links = document.querySelectorAll('.col-grid .col-grid__load-more');
    for (var i = 0; i < links.length; i++) {
      if (links[i].__colGridPrefetch) continue;
      links[i].__colGridPrefetch = true;
      prefetchObserver.observe(links[i]);
    }
  }

  // The link now points at the following page but may never leave the
  // observed band (short pages, wide screens), so no new intersection would
  // fire. Re-observing reports the current state straight away.
  function reobserve(link) {
    if (!prefetchObserver || !link.__colGridPrefetch) return;
    prefetchObserver.unobserve(link);
    prefetchObserver.observe(link);
  }

  // Moves the fetched page's cards into the live grid and hands back that
  // page's own Load more link (null on the last page).
  function appendPage(section, doc) {
    var grid = section.querySelector('.col-grid__items');
    var incoming = doc.querySelector('.col-grid__items');
    if (grid && incoming) {
      while (incoming.firstElementChild) grid.appendChild(incoming.firstElementChild);
    }
    return doc.querySelector('.col-grid__load-more');
  }

  // Point the live link at whatever comes next, or retire the whole control.
  function advance(link, nextLink) {
    if (!link) return;
    if (nextLink) {
      link.setAttribute('href', withoutSectionParams(nextLink.getAttribute('href')));
      setBusy(link, false);
      reobserve(link);
    } else {
      if (prefetchObserver) prefetchObserver.unobserve(link);
      var pagination = link.closest('.col-grid__pagination');
      if (pagination) pagination.remove();
    }
  }

  document.addEventListener('click', function (e) {
    // Any .col-grid on the page - this file is the one implementation behind
    // both the collection grid and the search results grid.
    var link = e.target.closest('.col-grid .col-grid__load-more');
    if (!link) return;
    // Leave modified clicks alone - the href is a real URL worth opening.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    e.preventDefault();
    if (link.getAttribute('aria-disabled') === 'true') return;

    var url = withoutSectionParams(link.getAttribute('href'));
    var section = link.closest('.col-grid');
    setBusy(link, true);

    fetchPage(url, section)
      .then(function (doc) {
        advance(link, appendPage(section, doc));
        writeState(pageOf(url), window.scrollY);
      })
      .catch(function () {
        window.location.href = url; // fall back to the real page
      });
  });

  // The position is only worth saving once more than one page is open - and it
  // has to be read on the way out, not at click time, because the customer
  // keeps scrolling after the last Load more. pagehide covers both a normal
  // navigation and going into the back/forward cache.
  window.addEventListener('pagehide', function () {
    var state = readState();
    if (state && state.pages > 1) writeState(state.pages, window.scrollY);
  });

  function isBackForward() {
    var entries = performance.getEntriesByType && performance.getEntriesByType('navigation');
    if (entries && entries.length) return entries[0].type === 'back_forward';
    return performance.navigation && performance.navigation.type === 2;
  }

  function restore() {
    var section = document.querySelector('.col-grid');
    if (!section) return;

    // A direct hit on ?page=3 is its own page - it has nothing to rebuild.
    if (new URL(window.location.href).searchParams.get('page')) return;

    if (!isBackForward()) {
      // A fresh visit starts the run over, so a Back later on doesn't rebuild
      // pages the customer never opened this time.
      clearState();
      return;
    }

    var state = readState();
    if (!state || !state.pages || state.pages < 2) return;

    var link = section.querySelector('.col-grid__load-more');
    var last = Math.min(state.pages, RESTORE_CAP);
    var urls = [];
    for (var p = 2; p <= last; p++) urls.push(pageUrl(p));

    setBusy(link, true);

    // Fetched in parallel, appended in order: the grid must come back in the
    // same sequence the customer built it in. A page that fails resolves to
    // null rather than rejecting - one throttled request out of nine (the
    // storefront does throttle bursts) must not cost the whole rebuild.
    Promise.all(
      urls.map(function (u) {
        return fetchPage(u, section).catch(function () {
          return null;
        });
      })
    )
      .then(function (docs) {
        var nextLink = null;
        var restored = 1;
        for (var i = 0; i < docs.length; i++) {
          // Stop at the first gap. Appending past a failed page would leave
          // the grid out of order with a block of products silently missing;
          // stopping short just leaves Load more pointing at the page that
          // failed, so the customer can retry it.
          if (!docs[i]) break;
          nextLink = appendPage(section, docs[i]);
          restored = i + 2;
        }

        if (restored < 2) {
          // Nothing came back. Leave the control alone - advance() would read
          // the null next link as "last page" and delete it.
          setBusy(link, false);
          return;
        }

        advance(link, nextLink);
        writeState(restored, state.scroll);

        if (typeof state.scroll !== 'number' || state.scroll <= 0) return;
        // The browser already clamped its own restore to the short page, so we
        // re-apply once the appended rows have laid out. Twice: the trim pass
        // that hides a partial last row runs on a timeout of its own.
        var jump = function () {
          window.scrollTo(0, state.scroll);
        };
        jump();
        requestAnimationFrame(jump);
        setTimeout(jump, 150);
      })
      .catch(function () {
        setBusy(link, false);
      });
  }

  // Exposed for the two filter bars. A filter or sort change swaps the grid
  // back to page 1 under a different URL, so whatever run was recorded for
  // that URL no longer describes what is on screen - and restore() only runs
  // at DOMContentLoaded, so it never sees the transition. Left uncalled, a
  // customer who loads four pages, filters, then clears the filter would come
  // back from a product to four rebuilt pages having left with one.
  //
  // Call it AFTER the URL has changed: the key is read from location.
  window.colGridPagination = {
    reset: clearState
  };

  function init() {
    watch();
    restore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('resize', trimAll);

})();
